import React from 'react';
import { Box, Typography, IconButton, Checkbox, Pagination } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { TFunction } from 'react-i18next';
import BlankModal from '../BlankModal';
import { PaginationInterface } from '../../types/Pagination';
import ReturnButton from '../ReturnButton';
import SearchBox from '../SearchBox';
import { LoadingButton } from '..';

interface AddLinkedItemFormProps {
  isVisible: boolean;
  results: Array<unknown>;
  title: string;
  searchButtonAriaLabel: string;
  handleClose: () => void;
  submitButton: {
    onClick: () => void;
    text: string;
    disabled: boolean;
    loading?: boolean;
  };
  pagination: PaginationInterface;
  searchFreeText: string;
  setSearchFreeText: (freeText: string) => void;
  isSmallDevice: boolean;
  t: TFunction<'translation'>;
  listItemGenerator: (item: unknown, idx: number) => JSX.Element;
  selectAllCheckbox?: {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    checked: boolean;
  };
  renderSpecial?: () => JSX.Element;
}

const AddLinkedItemForm: React.FC<AddLinkedItemFormProps> = ({
  results,
  isSmallDevice,
  pagination,
  searchFreeText,
  setSearchFreeText,
  title,
  searchButtonAriaLabel,
  handleClose,
  t,
  selectAllCheckbox,
  submitButton,
  isVisible,
  listItemGenerator,
  renderSpecial,
}: AddLinkedItemFormProps) => {
  const SubmitButton = (
    <LoadingButton
      color="primary"
      variant="contained"
      onClick={submitButton.onClick}
      data-testid="add-linked-item-submit-button"
      disabled={submitButton?.loading || submitButton.disabled}
      isLoading={submitButton?.loading}
      sx={{ mr: 2 }}
    >
      {submitButton.text}
    </LoadingButton>
  );

  const PaginationElement = (
    <Pagination
      page={pagination.page}
      count={pagination.pageCount}
      onChange={pagination.onChange}
      sx={{
        '& ul': { justifyContent: 'center' },
      }}
      data-testid="add-linked-item-form-pagination"
    />
  );

  return (
    <BlankModal
      // set height to 702px (!isSmallDevices only) to match abstract design, but limit max to 90% so it shrinks with screen
      sx={{ overflow: 'hidden', maxHeight: '90%', height: '702px' }}
      isSmallDevice={isSmallDevice}
      open={isVisible}
      handleClose={handleClose}
      showCloseButton={false}
    >
      <Box display="flex" sx={{ flexFlow: 'column', height: '100%' }}>
        <Box
          sx={{
            flex: '0 0 auto',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          {!isSmallDevice ? (
            <>
              <Typography variant="h5" component="h2">
                {title}
              </Typography>
              <IconButton data-testid="close-icon-button" onClick={handleClose} sx={{ margin: 0, padding: 0 }}>
                <Close />
              </IconButton>
            </>
          ) : null}
          {isSmallDevice ? (
            <>
              <Box>
                <ReturnButton dataTestId="back-button" onClick={handleClose} />
              </Box>
              {SubmitButton}
            </>
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {selectAllCheckbox ? (
            <>
              <Typography>{t(`ns.common:checkbox:select_all`)}</Typography>
              <Checkbox
                checked={selectAllCheckbox.checked}
                onChange={selectAllCheckbox.onChange}
                data-testid="select-all"
              />
            </>
          ) : null}
        </Box>
        <SearchBox
          label={searchButtonAriaLabel}
          onSubmit={(searchBoxValue) => setSearchFreeText(searchBoxValue)}
          startValue={searchFreeText}
          searchButtonAria={searchButtonAriaLabel}
        />

        {renderSpecial && <Box>{renderSpecial()}</Box>}

        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {results &&
            results?.map((result, idx) => {
              return listItemGenerator(result, idx);
            })}
        </Box>

        {!isSmallDevice ? (
          <Box width="100%" display="flex" justifyContent="space-between" pt={2}>
            <Box width="20%" />
            <Box width="60%">{PaginationElement}</Box>
            <Box width="20%" display="flex" justifyContent="flex-end" mr={4}>
              {SubmitButton}
            </Box>
          </Box>
        ) : (
          PaginationElement
        )}
      </Box>
    </BlankModal>
  );
};

export default AddLinkedItemForm;
